import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {QueryClient} from "@tanstack/react-query";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    AnalyticalEngineAttributeRole,
    getDatasetDataRequestValidator,
    getDatasetDataResponseValidator,
    getDatasetDetailsRequestValidator,
    getDatasetDetailsResponseValidator,
    getDistinctValuesRequestValidator,
    getDistinctValuesResponseValidator,
    listDatasetsRequestValidator,
    listDatasetsResponseValidator,
    type GetDatasetDataRequest,
    type GetDatasetDataResponse,
    type GetDatasetDetailsResponse,
    type GetDistinctValuesRequest,
    type GetDistinctValuesResponse,
    type ListDatasetsResponse,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/analyticalEngineWorkspaceCapability/domain/AnalyticalEngine";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {ProductCatalogue} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogue";
import type {ProductCatalogueTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/productCataloguesWorkspaceCapability/domain/ProductCatalogueTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {GeographyContextData} from "~/contexts/geographyContext";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {
    ANALYTICAL_ENGINE_DATA_QUERY_KEY,
    ANALYTICAL_ENGINE_DATASET_DETAILS_QUERY_KEY,
    ANALYTICAL_ENGINE_DATASETS_QUERY_KEY,
    ANALYTICAL_ENGINE_DISTINCT_VALUES_QUERY_KEY,
} from "~/utilities/queryKeys";
import {getUserDisplayName, type PlatformUserDisplayDetails} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {getEventContainerDispositionOptions, getEventDispositionOptions} from "~/utilities/ottopilotSemanticColumns";

export type AnalyticalEngineDataTransformationContext = {
    eventContainerTemplates: Array<EventContainerTemplate>;
    eventTemplates: Array<OttopilotEventTemplate>;
    userDefinedObjectOptionsByTemplateId: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>;
    geography: GeographyContextData;
    organizationalUnits: Array<OrganizationalUnit>;
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
    platformUserDetails: Array<PlatformUserDisplayDetails>;
    productCatalogues: Array<ProductCatalogue>;
    productCatalogueTemplates: Array<ProductCatalogueTemplate>;
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
};

type TemplatedInstance = {
    additionalColumnValues: Record<Uuid, unknown>;
    id: Uuid;
    templateId: Uuid;
};

type InstanceDisplayNameTemplate = {
    id: Uuid;
    instanceDisplayNameTemplate: string;
};

function resolveTemplatedInstanceDisplayName(
    rawValue: string | number | boolean | null,
    instances: Array<TemplatedInstance>,
    templates: Array<InstanceDisplayNameTemplate>,
    semanticTemplateId: Uuid | null,
): string | number | boolean | null {
    if (typeof rawValue !== "string") {
        return rawValue;
    }

    const instance = instances.find((candidate) => candidate.id === rawValue);
    if (instance === undefined || (semanticTemplateId !== null && instance.templateId !== semanticTemplateId)) {
        return rawValue;
    }

    const template = templates.find((candidate) => candidate.id === instance.templateId);
    if (template === undefined) {
        return rawValue;
    }

    const displayName = processOttopilotStringTemplate(template.instanceDisplayNameTemplate, instance.additionalColumnValues).trim();
    return displayName === "" ? rawValue : displayName;
}

function resolveUserDisplayName(
    rawValue: string | number | boolean | null,
    semanticTemplateId: Uuid | null,
    context: AnalyticalEngineDataTransformationContext,
    platformUserDetailsByUserId: ReadonlyMap<Uuid, PlatformUserDisplayDetails>,
): string | number | boolean | null {
    if (typeof rawValue !== "string") {
        return rawValue;
    }

    const user = context.users.find((candidate) => candidate.userId === rawValue || candidate.id === rawValue);
    if (user === undefined || (semanticTemplateId !== null && user.templateId !== semanticTemplateId)) {
        return rawValue;
    }

    const displayName = getUserDisplayName(user.userId, context.users, context.userTemplates, platformUserDetailsByUserId);
    if (displayName !== "-" && displayName !== user.userId) {
        return displayName;
    }

    const templatedDisplayName = resolveTemplatedInstanceDisplayName(user.id, [user], context.userTemplates, semanticTemplateId);
    return templatedDisplayName === user.id ? rawValue : templatedDisplayName;
}

export function transformAnalyticalEngineDatasetData(getAdditionalColumns: CapabilityUdoColumnResolver,
    data: GetDatasetDataResponse,
    context: AnalyticalEngineDataTransformationContext
): GetDatasetDataResponse {
    const platformUserDetailsByUserId = new Map(
        context.platformUserDetails.map((platformUserDetails) => [platformUserDetails.userId, platformUserDetails]),
    );
    const transformedData: GetDatasetDataResponse = {
        ...data,
        dataPoints: data.dataPoints.map((dataPoint) => dataPoint.map((cell) => ({...cell}))),
    };

    for (const dataPoint of transformedData.dataPoints) {
        for (const column of transformedData.columnMetadata) {
            const cell = dataPoint[column.columnIndex];
            if (cell === undefined) {
                continue;
            }

            const rawValue = cell.value;
            switch (column.semantic.attributeRole) {
                case AnalyticalEngineAttributeRole.default:
                case AnalyticalEngineAttributeRole.geographyLatitude:
                case AnalyticalEngineAttributeRole.geographyLongitude: {
                    break;
                };

                // TODO: Resolve enumeration labels from the semantic source, template, and additional column.
                case AnalyticalEngineAttributeRole.enumeration: {
                    break;
                };

                // TODO: Resolve user-defined-object references once their frontend data source is available.
                case AnalyticalEngineAttributeRole.userDefinedObject: {
                    break;
                };

                case AnalyticalEngineAttributeRole.organizationalUnit: {
                    cell.value = resolveTemplatedInstanceDisplayName(
                        rawValue,
                        context.organizationalUnits,
                        context.organizationalUnitTemplates,
                        column.semantic.organizationalUnitTemplateId,
                    );
                    break;
                };

                case AnalyticalEngineAttributeRole.user: {
                    cell.value = resolveUserDisplayName(
                        rawValue,
                        column.semantic.userTemplateId,
                        context,
                        platformUserDetailsByUserId,
                    );
                    break;
                };

                case AnalyticalEngineAttributeRole.productCatalogue: {
                    cell.value = resolveTemplatedInstanceDisplayName(
                        rawValue,
                        context.productCatalogues,
                        context.productCatalogueTemplates,
                        column.semantic.productCatalogueTemplateId,
                    );
                    break;
                };

                case AnalyticalEngineAttributeRole.geographyCountries: {
                    if (typeof rawValue === "string") {
                        cell.value = context.geography.countriesById.get(rawValue as Uuid)?.name ?? rawValue;
                    }
                    break;
                };

                case AnalyticalEngineAttributeRole.geographyIndiaCities: {
                    if (typeof rawValue === "string") {
                        cell.value = context.geography.indiaCitiesById.get(rawValue as Uuid)?.name ?? rawValue;
                    }
                    break;
                };

                case AnalyticalEngineAttributeRole.geographyIndiaPincodes: {
                    if (typeof rawValue === "string") {
                        cell.value = context.geography.indiaPincodesById.get(rawValue as Uuid)?.pincode ?? rawValue;
                    }
                    break;
                };

                case AnalyticalEngineAttributeRole.geographyIndiaStates: {
                    if (typeof rawValue === "string") {
                        cell.value = context.geography.indiaStatesById.get(rawValue as Uuid)?.name ?? rawValue;
                    }
                    break;
                };

                case AnalyticalEngineAttributeRole.geographyIndiaZones: {
                    if (typeof rawValue === "string") {
                        cell.value = context.geography.indiaZonesById.get(rawValue as Uuid)?.name ?? rawValue;
                    }
                    break;
                };

                case AnalyticalEngineAttributeRole.eventDisposition: {
                    const eventTemplateId = column.semantic.eventTemplateId;
                    const eventTemplate = context.eventTemplates.find((template) => template.id === eventTemplateId);
                    if (typeof rawValue === "string") {
                        cell.value = eventTemplate === undefined
                            ? rawValue
                            : getEventDispositionOptions(getAdditionalColumns, eventTemplate, context.userDefinedObjectOptionsByTemplateId)
                                .find((disposition) => disposition.id === rawValue)?.displayName ?? rawValue;
                    }
                    break;
                };

                case AnalyticalEngineAttributeRole.eventContainerDisposition: {
                    const eventContainerTemplateId = column.semantic.eventContainerTemplateId;
                    const eventContainerTemplate = context.eventContainerTemplates.find((template) => template.id === eventContainerTemplateId);
                    if (typeof rawValue === "string") {
                        cell.value = eventContainerTemplate === undefined
                            ? rawValue
                            : getEventContainerDispositionOptions(getAdditionalColumns, eventContainerTemplate, context.userDefinedObjectOptionsByTemplateId)
                                .find((disposition) => disposition.id === rawValue)?.displayName ?? rawValue;
                    }
                    break;
                };
            }
        }
    }

    return transformedData;
}

export async function getAnalyticalEngineDatasetsWithCaching({
    accessToken,
    intellsysBaseUrl,
    queryClient,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    queryClient: QueryClient;
    workspaceId: Uuid;
}): Promise<Array<ListDatasetsResponse[number]>> {
    return queryClient.ensureQueryData({
        queryKey: [ANALYTICAL_ENGINE_DATASETS_QUERY_KEY, workspaceId],
        queryFn: async (): Promise<ListDatasetsResponse> => {
            const request = listDatasetsRequestValidator.parse({workspaceId});
            const result = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/erato/analytics/datasets`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(request),
                },
                listDatasetsResponseValidator,
            );
            if (result.success !== true) {
                throw result.error;
            }
            return result.data;
        },
        retry: false,
        staleTime: Infinity,
    });
}

export async function getAnalyticalEngineDatasetDetailsWithCaching({
    accessToken,
    datasetId,
    intellsysBaseUrl,
    queryClient,
    workspaceId,
}: {
    accessToken: string;
    datasetId: Uuid;
    intellsysBaseUrl: string;
    queryClient: QueryClient;
    workspaceId: Uuid;
}): Promise<GetDatasetDetailsResponse> {
    return queryClient.ensureQueryData({
        queryKey: [ANALYTICAL_ENGINE_DATASET_DETAILS_QUERY_KEY, workspaceId, datasetId],
        queryFn: async (): Promise<GetDatasetDetailsResponse> => {
            const request = getDatasetDetailsRequestValidator.parse({datasetId, workspaceId});
            const result = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/erato/analytics/dataset-details`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(request),
                },
                getDatasetDetailsResponseValidator,
            );
            if (result.success !== true) {
                throw result.error;
            }
            return result.data;
        },
        retry: false,
        staleTime: Infinity,
    });
}

export async function getAnalyticalEngineDistinctValuesWithCaching({
    accessToken,
    intellsysBaseUrl,
    queryClient,
    request,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    queryClient: QueryClient;
    request: GetDistinctValuesRequest;
}): Promise<GetDistinctValuesResponse> {
    return queryClient.ensureQueryData({
        queryKey: [ANALYTICAL_ENGINE_DISTINCT_VALUES_QUERY_KEY, request.workspaceId, request.datasetId, request.attributeId, request.limit],
        queryFn: async (): Promise<GetDistinctValuesResponse> => {
            const validatedRequest = getDistinctValuesRequestValidator.parse(request);
            const result = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/erato/analytics/dataset/distinct-values`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(validatedRequest),
                },
                getDistinctValuesResponseValidator,
            );
            if (result.success !== true) {
                throw result.error;
            }
            return result.data;
        },
        retry: false,
        staleTime: Infinity,
    });
}

export async function getAnalyticalEngineDatasetDataWithCaching({
    accessToken,
    intellsysBaseUrl,
    queryClient,
    request,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    queryClient: QueryClient;
    request: GetDatasetDataRequest;
}): Promise<GetDatasetDataResponse> {
    return queryClient.ensureQueryData({
        queryKey: [
            ANALYTICAL_ENGINE_DATA_QUERY_KEY,
            request.workspaceId,
            request.datasetId,
            request.accountId,
            request.timeRange,
            request.metrics,
            request.dimensions,
            request.filter,
            request.orderBy,
            request.limit,
        ],
        queryFn: async (): Promise<GetDatasetDataResponse> => {
            const validatedRequest = getDatasetDataRequestValidator.parse(request);
            const result = await robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/erato/analytics/dataset/get-data`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(validatedRequest),
                },
                getDatasetDataResponseValidator,
            );
            if (result.success !== true) {
                throw result.error;
            }
            return result.data;
        },
        retry: false,
        staleTime: 0,
    });
}

export async function getResolvedAnalyticalEngineDatasetDataWithCaching(getAdditionalColumns: CapabilityUdoColumnResolver, {
    accessToken,
    intellsysBaseUrl,
    queryClient,
    request,
    transformationContext,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    queryClient: QueryClient;
    request: GetDatasetDataRequest;
    transformationContext: AnalyticalEngineDataTransformationContext;
}): Promise<GetDatasetDataResponse> {
    const data = await getAnalyticalEngineDatasetDataWithCaching({
        accessToken,
        intellsysBaseUrl,
        queryClient,
        request,
    });

    return transformAnalyticalEngineDatasetData(getAdditionalColumns, data, transformationContext);
}
